// src/hooks/useIsMobileBrowser.js
import { useState, useEffect } from 'react';

// Phones are detected two ways, because either signal alone has a hole:
//
//   1. User agent - catches phones in their default mode, but "Request Desktop
//      Site" rewrites the UA to a Mac/Windows one, so this misses those.
//   2. Hardware capability - `pointer: coarse` + `hover: none` + the physical
//      screen size. Desktop mode does NOT fake these, so it catches what the
//      UA check misses.
//
// Tablets are deliberately allowed, so the capability branch also requires a
// phone-sized physical screen. `screen.width/height` reports the device's own
// dimensions and (unlike `innerWidth`) is not changed by desktop mode's wider
// viewport, so an iPad stays an iPad.
//
// Touchscreen laptops are not caught: their primary pointer is a trackpad, so
// they report `pointer: fine` / `hover: hover` even though maxTouchPoints > 0.

const MOBILE_UA = /Android.+Mobile|webOS|iPhone|iPod|BlackBerry|BB10|IEMobile|Opera Mini|Windows Phone/i;

// Largest logical short-edge we treat as a phone. Current large phones are
// ~430px (iPhone Pro Max); the smallest tablets are 768px.
const PHONE_MAX_SHORT_EDGE = 500;

const matches = (query) => {
    try {
        return typeof window !== 'undefined'
            && typeof window.matchMedia === 'function'
            && window.matchMedia(query).matches;
    } catch {
        return false;
    }
};

export const isMobileBrowser = () => {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;

    // 1. Phone user agent, reported honestly.
    if (MOBILE_UA.test(navigator.userAgent || '')) return true;

    // 2. A touch-primary device with a phone-sized screen - i.e. a phone whose
    //    UA has been masked by desktop mode.
    const isTouchPrimary = matches('(pointer: coarse)') && matches('(hover: none)');
    if (!isTouchPrimary) return false;

    const screen = window.screen || {};
    const shortEdge = Math.min(screen.width || 0, screen.height || 0);
    if (!shortEdge) return false; // no usable signal - don't block

    return shortEdge <= PHONE_MAX_SHORT_EDGE;
};

/**
 * True when the portal is being viewed in a phone browser, including phones
 * using "Request Desktop Site".
 */
export const useIsMobileBrowser = () => {
    const [isMobile, setIsMobile] = useState(isMobileBrowser);

    useEffect(() => {
        const recheck = () => setIsMobile(isMobileBrowser());
        window.addEventListener('orientationchange', recheck);
        window.addEventListener('resize', recheck);
        return () => {
            window.removeEventListener('orientationchange', recheck);
            window.removeEventListener('resize', recheck);
        };
    }, []);

    return isMobile;
};

export default useIsMobileBrowser;
